import { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { 
  FileText, BookOpen, Edit2, Save, X, Loader2, Plus, 
  Upload, Trash2, Hash, Mic, LayoutTemplate, User, Sparkles, Copy, CheckCircle2, AlertTriangle, Target
} from 'lucide-react';
import { AppLayout } from '../components/layout/AppLayout';
import { cn } from '../lib/utils';
import { auth, db } from '../services/firebase';
import { 
  StrategyDoc, getOrSeedStrategyDocs, updateStrategyDoc, 
  createStrategyDoc, deleteStrategyDoc 
} from '../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Minimal Project interface for dependency tracking
interface Project {
  id: string;
  name: string;
  voiceId?: string;
  personaId?: string;
  frameworkId?: string;
}

// ─── E-E-A-T STANDARD TEMPLATES ──────────────────────────────────────────────
const TEMPLATE_AEO_ARC = `### The AEO Insight Arc (Google E-E-A-T Compliant)

**1. The Context (The Goal):**
State exactly what we were trying to build or achieve. Keep it brief.

**2. The Bottleneck (Experience):**
Explain the specific, highly-technical roadblock we hit. *Rule: This proves we actually did the work.*

**3. The Pivot (Authenticity):**
Explain the failed attempts or initial naive assumptions. *Rule: Show the scars.*

**4. The Architecture (Expertise):**
Explain the final solution using precise metrics, tool names, and constraints.

**5. The Takeaway (Helpfulness):**
One concluding sentence on why this matters to the reader and how they can apply it to avoid the same bottleneck.`;

const TEMPLATE_PRACTITIONER = `### The Technical Practitioner

**Core Identity:** You are a senior builder and pragmatic founder. You execute daily and share reality.

**Psychological Bias:**
You value execution over theory. You despise marketing fluff, "growth hacking," and generic listicles. You believe that the code, the revenue, and the architecture speak for themselves.

**Rules of Engagement:**
* **First-Hand Only:** Speak ONLY from direct experience. Never give generic advice you haven't personally validated.
* **Humble Authority:** Position yourself as an authority by sharing the depths of your technical challenges, not by bragging about the outcome.
* **Reader Goal:** Your ultimate goal is to help a peer solve a hard problem faster by giving them the exact blueprints you used.`;

const TEMPLATE_ZERO_FLUFF = `### High-Signal / Zero-Fluff

**Vocabulary Rules:**
* **BANNED WORDS:** Thrilled, excited, revolutionary, game-changer, unlock, leverage, supercharge, dive in.
* **BANNED FORMATS:** Do not use emojis. Do not use excessive exclamation points. 
* **REQUIRED FORMATS:** Use bullet points for technical lists. Use bold text only for core architectural concepts.

**Tonal Directives:**
* **Information Density:** Optimize for maximum facts per sentence. Strip out adjectives.
* **Active Voice:** "We migrated the database," not "The database was migrated."
* **Direct and Clinical:** State the problem, state the constraint, state the solution. Treat the reader like an intelligent peer who is short on time.`;

export function Strategy() {
  const [docs, setDocs] = useState<StrategyDoc[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [activeDoc, setActiveDoc] = useState<StrategyDoc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Editing State
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTags, setEditTags] = useState(''); 
  const [isSaving, setIsSaving] = useState(false);

  // New Doc Modal State
  const [showNewDocModal, setShowNewDocModal] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [newDocType, setNewDocType] = useState<'voice' | 'persona' | 'framework'>('framework');
  const [newDocContent, setNewDocContent] = useState('');
  const [newDocTags, setNewDocTags] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Workflow State
  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    document.title = 'Buildlog — Narrative Engine';
    const loadData = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const [fetchedDocs, projectsSnapshot] = await Promise.all([
          getOrSeedStrategyDocs(user.uid),
          getDocs(query(collection(db, 'projects'), where('userId', '==', user.uid)))
        ]);
        
        setDocs(fetchedDocs);
        setProjects(projectsSnapshot.docs.map(d => ({ id: d.id, ...d.data() } as Project)));
        
        if (fetchedDocs.length > 0) setActiveDoc(fetchedDocs[0]);
      } catch (error) {
        console.error("Failed to load strategy data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const handleEditToggle = () => {
    if (!activeDoc) return;
    setEditContent(activeDoc.content);
    setEditTags(activeDoc.tags?.join(', ') || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!activeDoc) return;
    setIsSaving(true);
    try {
      const tagArray = editTags.split(',').map(t => t.trim()).filter(Boolean);
      await updateStrategyDoc(activeDoc.id, editContent, tagArray, activeDoc.type); 
      
      const updatedDoc = { ...activeDoc, content: editContent, tags: tagArray };
      setActiveDoc(updatedDoc);
      setDocs(docs.map(d => d.id === updatedDoc.id ? updatedDoc : d));
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save strategy:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadedFileName(file.name);
    if (!newDocTitle.trim()) {
      const nameWithoutExt = file.name.replace(/\.(md|txt)$/i, '');
      setNewDocTitle(nameWithoutExt);
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setNewDocContent(ev.target?.result as string ?? '');
    };
    reader.readAsText(file);
  };

  const handleClearFile = () => {
    setUploadedFileName('');
    setNewDocContent('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeleteDoc = async () => {
    if (!confirmDeleteId) return;
    setIsDeleting(true);
    try {
      await deleteStrategyDoc(confirmDeleteId);
      const remaining = docs.filter(d => d.id !== confirmDeleteId);
      setDocs(remaining);
      if (activeDoc?.id === confirmDeleteId) {
        setActiveDoc(remaining.length > 0 ? remaining[0] : null);
        setIsEditing(false);
      }
      setConfirmDeleteId(null);
    } catch (error) {
      console.error('Failed to delete doc:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseModal = () => {
    setShowNewDocModal(false);
    setNewDocTitle('');
    setNewDocType('framework');
    setNewDocContent('');
    setNewDocTags('');
    setUploadedFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleLoadTemplate = (type: 'framework' | 'persona' | 'voice') => {
    let title = '';
    let content = '';
    let tags = 'aeo, verified';
    
    if (type === 'framework') {
      title = 'AEO Insight Arc';
      content = TEMPLATE_AEO_ARC;
    } else if (type === 'persona') {
      title = 'The Technical Practitioner';
      content = TEMPLATE_PRACTITIONER;
    } else if (type === 'voice') {
      title = 'High-Signal / Zero-Fluff';
      content = TEMPLATE_ZERO_FLUFF;
    }

    setNewDocType(type);
    setNewDocTitle(title);
    setNewDocContent(content);
    setNewDocTags(tags);
    setShowNewDocModal(true);
  };

  const handleCreateDoc = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDocTitle.trim() || !auth.currentUser) return;

    setIsCreating(true);
    try {
      const tagArray = newDocTags.split(',').map(t => t.trim()).filter(Boolean);
      let newDoc = await createStrategyDoc(auth.currentUser.uid, newDocTitle.trim(), newDocType);
      
      if (newDocContent || tagArray.length > 0) {
        await updateStrategyDoc(newDoc.id, newDocContent, tagArray, newDocType);
        newDoc = { ...newDoc, content: newDocContent, tags: tagArray };
      }
      
      setDocs([...docs, newDoc]);
      setActiveDoc(newDoc);
      setIsEditing(false);
      handleCloseModal();
    } catch (error) {
      console.error("Failed to create doc:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const copyPrompt = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedPromptId(id);
    setTimeout(() => setCopiedPromptId(null), 2000);
  };

  // NARRATIVE ENGINE CATEGORIZATION (Sorted Alphabetically)
  const sortByTitle = (a: StrategyDoc, b: StrategyDoc) => a.title.localeCompare(b.title);
  const voices = docs.filter(d => d.type === 'voice').sort(sortByTitle);
  const personas = docs.filter(d => d.type === 'persona' || (!d.type && d.title.startsWith('Persona:'))).sort(sortByTitle);
  const frameworks = docs.filter(d => d.type === 'framework' || (!d.type && !d.title.startsWith('Persona:'))).sort(sortByTitle);

  const getDependentProjects = (docId: string) => {
    return projects.filter(p => 
      p.voiceId === docId || p.personaId === docId || p.frameworkId === docId
    );
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-40">
          <Loader2 className="w-8 h-8 text-slate-900 animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const activeDocDependencies = activeDoc ? getDependentProjects(activeDoc.id) : [];
  const deleteTargetDependencies = confirmDeleteId ? getDependentProjects(confirmDeleteId) : [];

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto space-y-8 pb-20">
        <header>
          <h1 className="text-3xl font-bold text-slate-900 font-display tracking-tight">Narrative Engine</h1>
          <p className="text-slate-500">Manage the Voice, Persona, and Story Frameworks powering Claude's output.</p>
        </header>

        <div className="flex flex-col md:flex-row gap-8 items-start">
          {/* Sidebar */}
          <aside className="w-full md:w-72 shrink-0 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col max-h-[calc(100vh-12rem)]">
            <div className="p-4 border-b border-slate-100 bg-slate-50 shrink-0">
              <h2 className="text-[11px] font-mono uppercase tracking-widest text-slate-900 flex items-center gap-2 font-bold">
                <BookOpen size={14} /> Knowledge Base
              </h2>
            </div>
            
            <div className="p-2 space-y-6 overflow-y-auto flex-1">
              <section className="space-y-1">
                <h3 className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-tighter flex items-center gap-1.5">
                  <Mic size={12}/> Brand Voices
                </h3>
                {voices.map(doc => renderDocLink(doc))}
                {voices.length === 0 && <p className="px-3 text-xs text-slate-400 italic">No voices defined.</p>}
              </section>

              <section className="space-y-1">
                <h3 className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-tighter flex items-center gap-1.5">
                  <LayoutTemplate size={12}/> Story Frameworks
                </h3>
                {frameworks.map(doc => renderDocLink(doc))}
                {frameworks.length === 0 && <p className="px-3 text-xs text-slate-400 italic">No frameworks defined.</p>}
              </section>

              <section className="space-y-1">
                <h3 className="px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-tighter flex items-center gap-1.5">
                  <User size={12}/> Project Personas
                </h3>
                {personas.map(doc => renderDocLink(doc))}
                {personas.length === 0 && <p className="px-3 text-xs text-slate-400 italic">No personas defined.</p>}
              </section>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 shrink-0 space-y-2">
              <button
                onClick={() => setShowNewDocModal(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-bold text-white bg-slate-900 hover:bg-slate-700 rounded-xl transition-all shadow-sm active:scale-95"
              >
                <Plus size={16} /> New Component
              </button>
              <button
                onClick={() => setActiveDoc(null)}
                className={cn("w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold rounded-lg transition-colors", activeDoc === null ? "bg-accent/10 text-accent" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900")}
              >
                <Sparkles size={14} /> AI Strategy Assistant
              </button>
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1 bg-white border border-slate-200 rounded-2xl flex flex-col min-h-[600px] overflow-hidden shadow-sm">
            {activeDoc ? (
              <>
                <div className="border-b border-slate-100 px-6 py-4 flex items-center justify-between bg-white">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-mono uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                        {activeDoc.type || 'framework'}
                      </span>
                      <h3 className="font-bold text-slate-900 font-display">{activeDoc.title}</h3>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2">
                      {activeDocDependencies.length > 0 ? (
                        <span className="flex items-center gap-1.5 px-2 py-0.5 bg-green-50 border border-green-100 text-green-700 rounded-md text-[10px] font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          Active in {activeDocDependencies.length} Project{activeDocDependencies.length > 1 ? 's' : ''} ({activeDocDependencies.map(p => p.name).join(', ')})
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-50 border border-slate-100 text-slate-500 rounded-md text-[10px] font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                          Not currently assigned to any projects
                        </span>
                      )}

                      {activeDoc.tags?.map(tag => (
                        <span key={tag} className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">#{tag}</span>
                      ))}
                    </div>
                  </div>
                  {!isEditing ? (
                    <button onClick={handleEditToggle} className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors px-4 py-2 border border-slate-200 rounded-lg shadow-sm">
                      <Edit2 size={14} /> Edit Doc
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button onClick={() => setIsEditing(false)} className="text-xs font-bold text-slate-400 hover:text-slate-900 px-4 py-2">Cancel</button>
                      <button onClick={handleSave} disabled={isSaving} className="flex items-center gap-2 text-xs font-bold text-white bg-slate-900 hover:bg-slate-700 px-4 py-2 rounded-lg disabled:opacity-50 shadow-sm">
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Component
                      </button>
                    </div>
                  )}
                </div>

                <div className="p-8 flex-1 overflow-y-auto">
                  {isEditing ? (
                    <div className="space-y-6 h-full flex flex-col">
                      <div className="space-y-1.5">
                        <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-2">
                          <Hash size={12} /> Auto-apply Tags (Comma-separated)
                        </label>
                        <input 
                          value={editTags}
                          onChange={(e) => setEditTags(e.target.value)}
                          className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-slate-900 outline-none"
                          placeholder="agentic, orchestration, safety"
                        />
                        <p className="text-[10px] text-slate-400">These tags will auto-populate on projects that use this component.</p>
                      </div>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="flex-1 w-full p-6 bg-slate-50 border border-slate-200 rounded-xl font-mono text-sm text-slate-900 focus:border-slate-900 outline-none resize-none min-h-[400px]"
                        placeholder="Define your instructions for Claude..."
                      />
                    </div>
                  ) : (
                    <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:text-slate-900 prose-p:text-slate-600 prose-strong:text-slate-900 prose-code:text-slate-900 prose-li:text-slate-600">
                      <ReactMarkdown>{activeDoc.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="p-8 flex-1 flex flex-col h-full bg-slate-50/50 overflow-y-auto relative">
                
                {/* Fingerprint Watermark */}
                <div 
                  className="absolute inset-0 pointer-events-none opacity-[0.02] mix-blend-multiply"
                  style={{ backgroundImage: `url('/fingerprint-pattern.svg')`, backgroundSize: '100%', backgroundPosition: 'center' }}
                />

                <div className="relative z-10 max-w-3xl mx-auto w-full space-y-10 pb-10">
                  
                  <div className="text-center space-y-3 pt-8">
                    <div className="w-12 h-12 bg-accent/10 border border-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                      <Sparkles className="text-accent" size={24} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 font-display">Initialize Your Strategy</h2>
                    <p className="text-slate-500 text-[15px] leading-relaxed max-w-xl mx-auto">
                      Use MCP to extract strategy from your past logs, reverse-engineer a high-signal founder, or deploy our automated <strong className="text-slate-900 font-medium">E-E-A-T</strong> templates directly.
                    </p>
                  </div>

                  {/* ── NEW: E-E-A-T TEMPLATES SECTION ── */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                    <div className="border-b border-slate-100 pb-4 mb-4">
                      <h3 className="font-bold text-slate-900 font-display flex items-center gap-2">
                        <Target size={18} className="text-accent" />
                        Deploy E-E-A-T Standard Templates
                      </h3>
                      <p className="text-sm text-slate-500 mt-1">
                        Instantly load constraints designed to satisfy Google's Answer Engine requirements (Experience, Expertise, Authoritativeness, Trustworthiness).
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button onClick={() => handleLoadTemplate('framework')} className="p-4 border border-slate-200 rounded-xl text-left hover:border-slate-900 hover:shadow-md transition-all group">
                        <LayoutTemplate size={16} className="text-slate-400 mb-2 group-hover:text-slate-900 transition-colors" />
                        <h4 className="font-bold text-sm text-slate-900 mb-1">AEO Insight Arc</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed">A 5-step framework guaranteeing high Information Gain.</p>
                      </button>
                      <button onClick={() => handleLoadTemplate('persona')} className="p-4 border border-slate-200 rounded-xl text-left hover:border-slate-900 hover:shadow-md transition-all group">
                        <User size={16} className="text-slate-400 mb-2 group-hover:text-slate-900 transition-colors" />
                        <h4 className="font-bold text-sm text-slate-900 mb-1">The Practitioner</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed">A founder persona optimized for first-hand experience.</p>
                      </button>
                      <button onClick={() => handleLoadTemplate('voice')} className="p-4 border border-slate-200 rounded-xl text-left hover:border-slate-900 hover:shadow-md transition-all group">
                        <Mic size={16} className="text-slate-400 mb-2 group-hover:text-slate-900 transition-colors" />
                        <h4 className="font-bold text-sm text-slate-900 mb-1">Zero-Fluff Voice</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed">Strict vocabulary rules to eliminate generic AI marketing.</p>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-4">
                    
                    {/* PATH A: Own Logs */}
                    <div className="space-y-6">
                      <div className="border-b border-slate-200 pb-3">
                        <h3 className="font-bold text-slate-900 font-display flex items-center gap-2">
                          <span className="w-6 h-6 rounded bg-slate-900 text-white flex items-center justify-center text-xs">A</span>
                          Analyze Your Past Logs
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">Copy prompts to use with the MCP <code className="text-[10px] bg-slate-100 px-1 rounded">getEntries</code> tool.</p>
                      </div>

                      <div className="space-y-4">
                        {[
                          { id: 'my-voice', title: 'Extract Brand Voice', prompt: 'Use the getEntries tool to read my last 10 build logs. Based on how I write and what I am building, define my specific "Brand Voice." Give me a markdown bulleted list of strict rules, vocabulary to use, and fluff to avoid.' },
                          { id: 'my-persona', title: 'Define Persona', prompt: 'Based on the logs you just read, who is my target audience? Create a "Professional Persona" document for me that explains what my core bias should be, what psychological triggers to hit, and my ultimate goal.' },
                          { id: 'my-framework', title: 'Build Story Arc', prompt: 'I want to turn my technical logs into engaging LinkedIn updates. Create a "Story Framework" document using a 3-Act structure (e.g., The Bottleneck, The Architecture, The Reality Check). Give me markdown rules for each act.' }
                        ].map((item) => (
                          <div key={item.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-slate-900 transition-colors group">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="font-bold text-sm text-slate-900">{item.title}</h4>
                              <button onClick={() => copyPrompt(item.id, item.prompt)} className="text-slate-500 hover:text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity">
                                {copiedPromptId === item.id ? <CheckCircle2 size={14} className="text-green-600"/> : <Copy size={14}/>}
                              </button>
                            </div>
                            <p className="font-mono text-[10px] text-slate-500 leading-relaxed line-clamp-2 group-hover:line-clamp-none transition-all">"{item.prompt}"</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* PATH B: Lookalike */}
                    <div className="space-y-6">
                      <div className="border-b border-slate-200 pb-3">
                        <h3 className="font-bold text-slate-900 font-display flex items-center gap-2">
                          <span className="w-6 h-6 rounded bg-slate-200 text-slate-900 flex items-center justify-center text-xs">B</span>
                          Analyze a High-Signal Founder
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">Copy prompts to reverse-engineer a successful creator.</p>
                      </div>

                      <div className="space-y-4">
                        {[
                          { id: 'steal-voice', title: 'Extract their Zero-Fluff Voice', prompt: 'I am going to paste 5 posts from a high-signal founder I admire. Analyze their tone. How do they strip out marketing fluff? How do they establish technical credibility? Output a "Brand Voice" ruleset in markdown that I can use to emulate their high-information-density style.' },
                          { id: 'steal-persona', title: 'Decode their Authority Persona', prompt: 'Based on the founder posts I just pasted, decode their "Professional Persona." How do they position themselves as an authority without bragging? What specific audience are they targeting (e.g., peers, developers)? Define this persona in a markdown document, including their core biases and how they build trust.' },
                          { id: 'steal-framework', title: 'Steal their Insight Arc', prompt: 'Analyze the structural pacing of the posts I pasted. How do they hook the reader using a concrete problem rather than clickbait? How do they transition into the technical reality, and how do they deliver the final proprietary insight? Create a reusable "Story Framework" in markdown based on their exact structural patterns.' }
                        ].map((item) => (
                          <div key={item.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:border-slate-900 transition-colors group">
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="font-bold text-sm text-slate-900">{item.title}</h4>
                              <button onClick={() => copyPrompt(item.id, item.prompt)} className="text-slate-500 hover:text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity">
                                {copiedPromptId === item.id ? <CheckCircle2 size={14} className="text-green-600"/> : <Copy size={14}/>}
                              </button>
                            </div>
                            <p className="font-mono text-[10px] text-slate-500 leading-relaxed line-clamp-2 group-hover:line-clamp-none transition-all">"{item.prompt}"</p>
                          </div>
                        ))}
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* NEW DOCUMENT MODAL */}
      {showNewDocModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900 font-display">New Strategy Component</h3>
              <button onClick={handleCloseModal} className="text-slate-400 hover:text-slate-900 transition-colors">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleCreateDoc} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Component Type</label>
                  <select 
                    value={newDocType}
                    onChange={(e) => setNewDocType(e.target.value as any)}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-900 font-medium text-sm"
                  >
                    <option value="voice">Brand Voice (The Tone & Rules)</option>
                    <option value="framework">Story Framework (The Objective Arc)</option>
                    <option value="persona">Professional Persona (The Lens/Role)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label htmlFor="title" className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Document Title</label>
                  <input
                    id="title"
                    type="text"
                    autoFocus
                    value={newDocTitle}
                    onChange={(e) => setNewDocTitle(e.target.value)}
                    placeholder="e.g., Theme: Agentic Autonomy"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-900 text-sm"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                  <Hash size={12} /> Auto-apply Tags <span className="text-slate-400 font-normal">(comma-separated)</span>
                </label>
                <input
                  type="text"
                  value={newDocTags}
                  onChange={(e) => setNewDocTags(e.target.value)}
                  placeholder="e.g., builders, infrastructure, ucp"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:border-slate-900 text-sm"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
                    Strategy Content <span className="text-slate-400 font-normal">(Markdown supported)</span>
                  </label>
                  
                  {!uploadedFileName ? (
                    <button 
                      type="button" 
                      onClick={() => fileInputRef.current?.click()} 
                      className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-2.5 py-1 rounded transition-colors"
                    >
                      <Upload size={12} /> Import .md File
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-mono text-green-600 font-bold truncate max-w-[150px]">Imported: {uploadedFileName}</span>
                      <button type="button" onClick={handleClearFile} className="flex items-center gap-1 text-[10px] font-bold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2 py-1 rounded transition-colors">
                        <X size={12} /> Clear
                      </button>
                    </div>
                  )}
                </div>
                
                <textarea
                  value={newDocContent}
                  onChange={(e) => setNewDocContent(e.target.value)}
                  className="w-full h-48 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-mono text-xs text-slate-900 focus:border-slate-900 outline-none resize-none"
                  placeholder="# Paste your strategy rules, framework arc, or persona guidelines here..."
                  required
                />
                <input ref={fileInputRef} type="file" accept=".md,.txt,text/markdown,text/plain" onChange={handleFileChange} className="hidden" />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50 rounded-lg">Cancel</button>
                <button type="submit" disabled={isCreating || !newDocTitle.trim() || !newDocContent.trim()} className="px-6 py-2 text-sm font-bold text-white bg-slate-900 hover:bg-slate-700 rounded-lg transition-all disabled:opacity-50">
                  {isCreating ? 'Creating...' : 'Create Component'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL WITH DEPENDENCY WARNING */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 text-center">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-50 mx-auto mb-4">
                <Trash2 size={20} className="text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">Delete Component?</h3>
              <p className="text-sm text-slate-500 mb-4">This cannot be undone.</p>

              {deleteTargetDependencies.length > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 p-3 rounded-xl text-left">
                  <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-amber-800">Warning: Active Component</p>
                    <p className="text-[11px] text-amber-700 leading-snug mt-0.5">
                      This component is currently powering {deleteTargetDependencies.length} project(s). Deleting it will remove the narrative constraints from: {deleteTargetDependencies.map(p => p.name).join(', ')}.
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 pb-6 flex justify-center gap-3">
              <button onClick={() => setConfirmDeleteId(null)} className="px-4 py-2 text-sm font-bold text-slate-500 hover:bg-slate-50 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleDeleteDoc} disabled={isDeleting} className="px-6 py-2 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2">
                {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}

    </AppLayout>
  );

  function renderDocLink(doc: StrategyDoc) {
    return (
      <div key={doc.id} className={cn('group flex items-center gap-1 rounded-xl transition-all', activeDoc?.id === doc.id ? 'bg-slate-50 border border-slate-200 shadow-sm' : 'hover:bg-slate-50/50 border border-transparent')}>
        <button onClick={() => { setActiveDoc(doc); setIsEditing(false); }} className={cn('flex-1 flex items-center gap-3 px-3 py-2 text-sm font-medium text-left min-w-0', activeDoc?.id === doc.id ? 'text-slate-900 font-bold' : 'text-slate-500 hover:text-slate-900')}>
          <FileText size={14} className={cn('shrink-0', activeDoc?.id === doc.id ? 'text-accent' : 'text-slate-300')} />
          <span className="truncate">{doc.title.replace('Persona: ', '').replace('Framework: ', '').replace('Voice: ', '')}</span>
        </button>
        <button onClick={() => setConfirmDeleteId(doc.id)} className="shrink-0 p-1.5 mr-1 text-slate-200 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all rounded-lg"><Trash2 size={14} /></button>
      </div>
    );
  }
}